import { FLOOR_FALL_MS } from "./floor_fall"
import { BODY_GROUND_OFFSET } from "./lpc/body_placement"
import { CollisionMover, Coordinate, Entity, FloorFallView, TileSize } from "./models"
import { STOREY_RISE_PX } from "./room/ember/two_storey_house"
import { HillHalo, HillLevels, hillCliffLevel, hillHeightAt, hillLevelsOf } from "./room/hill_height"
import { TileMapMetaProvider } from "./tile_map_meta_provider"

/**
 * CLIFFS AND WALKING (openspec world/cliffs). A cliff is where side-by-side hill cells stand two levels or more apart,
 * and the ground drops its whole height there at once (hill_height.ts). It is the one part of a hill a body cannot walk
 * over: a step of the feet up its face is refused, as at a wall, and a step off its rim is a drop, taken only by a body
 * that may drop (a player; a hostile that can leap down storeys) -- every other body is held at the rim. Every body's
 * mover goes through cliffMover, so client prediction, the server, NPC brains and pushes agree to the step.
 */

/**
 * How far the ground under the feet may rise or fall in one step before it is a cliff: a cliff is 64 px or more, a
 * slope climbs at most 3 px a px, and a step is a few px.
 */
export const CLIFF_STEP_PX = 48

/** How a body meets a cliff: "hold" stops it at a face and at a rim; "drop" stops it at a face and lets it walk off a rim. */
export type CliffWay = "hold" | "drop"

const levelsByLayer = new WeakMap<object, { halo: HillHalo | undefined; levels: HillLevels | null }>()

/** A room's hill levels with its border, when they hold a cliff: read once per `hill` layer and border. */
export const cliffLevelsOf = (map: Pick<TileMapMetaProvider, "getHillLayer" | "getHillHalo"> | undefined): HillLevels | undefined => {
    const layer = map?.getHillLayer?.(), halo = map?.getHillHalo?.()
    const key = layer ?? halo
    if (!key) return undefined
    let entry = levelsByLayer.get(key)
    if (!entry || entry.halo !== halo) {
        const levels = hillLevelsOf(layer, halo)
        levelsByLayer.set(key, entry = { halo, levels: levels?.cliffs ? levels : null })
    }
    return entry.levels ?? undefined
}

/**
 * Whether cell (cx, cy) is at a cliff: the 3x3 cells round it hold a gap of two levels or more. A rim cell, a foot cell,
 * and a cell diagonal to a cliff's corner (which the cliff's line shaves) all are; nothing is lit there (a campfire).
 */
export const cliffCell = (levels: HillLevels, cx: number, cy: number): boolean => {
    const window: number[] = []
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) window.push(levels.at(cx + dx, cy + dy))
    for (let L = 1; L <= levels.max; L++) if (hillCliffLevel(window, L)) return true
    return false
}

/** Whether the 3x3 cells round world point (x, y) hold a cliff: a gap of two levels or more. */
const cliffNear = (levels: HillLevels, x: number, y: number): boolean =>
    cliffCell(levels, Math.floor(x / TileSize), Math.floor(y / TileSize))

/** Whether a step of the feet from (x0, y0) to (x1, y1) climbs a cliff's face ("rise"), goes off its rim ("drop"), or neither. */
export const cliffStep = (levels: HillLevels, x0: number, y0: number, x1: number, y1: number): "rise" | "drop" | undefined => {
    const rise = hillHeightAt(levels, x1, y1) - hillHeightAt(levels, x0, y0)
    if (Math.abs(rise) <= CLIFF_STEP_PX) return undefined
    if (!cliffNear(levels, x0, y0) && !cliffNear(levels, x1, y1)) return undefined
    return rise > 0 ? "rise" : "drop"
}

// The march cliffFaceBetween takes along the ground between two bodies: fine enough to meet a face's line.
const FACE_MARCH_PX = 2

/**
 * Whether a cliff's face stands between two bodies at locations `a` and `b`, either way: on the ground between their
 * feet, a step up a face or off a rim. A melee strike reaches across neither (openspec world/cliffs, "A cliff's face
 * stops a melee strike"), however near the two stand.
 */
export const cliffFaceBetween = (levels: HillLevels, a: Coordinate, b: Coordinate): boolean => {
    const x0 = a.x, y0 = a.y + BODY_GROUND_OFFSET, dx = b.x - a.x, dy = b.y - a.y
    const steps = Math.ceil(Math.hypot(dx, dy) / FACE_MARCH_PX)
    for (let i = 0; i < steps; i++) {
        if (cliffStep(levels, x0 + dx * i / steps, y0 + dy * i / steps, x0 + dx * (i + 1) / steps, y0 + dy * (i + 1) / steps)) return true
    }
    return false
}

/** Where a body's feet stand and the ground's height there: what a fall off a rim is told by, pass to pass. */
export interface CliffGround {
    x: number
    y: number
    height: number
}

export const cliffGroundOf = (levels: HillLevels, location: { x: number; y: number }): CliffGround => {
    const x = location.x, y = location.y + BODY_GROUND_OFFSET
    return { x, y, height: hillHeightAt(levels, x, y) }
}

/**
 * How far a body has dropped off a cliff's rim between `last` and `now` (where its feet stood, and stand): the drop in
 * px, or 0 when it has not -- a move of a tile or more (a teleport) is never a fall.
 */
export const cliffDropBetween = (levels: HillLevels, last: CliffGround | undefined, now: CliffGround): number => {
    if (!last || Math.hypot(now.x - last.x, now.y - last.y) >= TileSize) return 0
    return cliffStep(levels, last.x, last.y, now.x, now.y) === "drop" ? last.height - now.height : 0
}

/**
 * The fall off a cliff's rim, as a fall from a storey that high (openspec world/cliffs): arriving in place, the body
 * drawn the drop above where it stands and coming down on the storeys' gravity, so `distance` takes
 * FLOOR_FALL_MS x the square root of its levels. Down two levels or more, it lands and lies a moment (server).
 */
export const cliffFallView = (id: string, roomId: string | undefined, distance: number): FloorFallView => {
    const gravity = 2 * STOREY_RISE_PX / (FLOOR_FALL_MS * FLOOR_FALL_MS)
    const durationMs = Math.sqrt(2 * distance / gravity)
    return { id, roomId, phase: "arriving", fromAbove: true, exterior: true, arrivalDurationMs: durationMs, durationMs,
        remainingMs: durationMs, distance, levelsFallen: Math.round(distance / STOREY_RISE_PX),
        trajectory: { gravity, distanceBefore: 0, endElapsedMs: durationMs, totalDistance: distance } }
}

/** Whether a body may walk off a cliff's rim: a player, or a hostile that can leap down storeys. */
export const mayDropOffCliff = (entity: Pick<Entity, "isPlayer" | "flags">): boolean =>
    entity.isPlayer() || (entity.flags?.canJumpStoreys === true && !entity.flags?.nonAggro)

/** The way a body meets cliffs (mayDropOffCliff). */
export const cliffWayOf = (entity: Pick<Entity, "isPlayer" | "flags">): CliffWay => (mayDropOffCliff(entity) ? "drop" : "hold")

/**
 * `mover` for a body, with cliffs: a step of its feet up a cliff's face is refused, and one off a rim too unless `way`
 * is "drop" and the ground at the foot can be stood on (deep water, a blocking tile, holds a body at the rim as the
 * sea's edge does; shallows do not). A room without a cliff gets `mover` back as it is.
 */
export const cliffMover = (map: TileMapMetaProvider, mover: CollisionMover, way: CliffWay): CollisionMover => {
    const levels = cliffLevelsOf(map)
    if (!levels) return mover
    return (bounds, translate, result, adjust) => {
        if (translate.x || translate.y) {
            const x = bounds.x1 + bounds.width / 2, y = bounds.y1 + bounds.height / 2
            const feet = y + BODY_GROUND_OFFSET
            const step = cliffStep(levels, x, feet, x + translate.x, feet + translate.y)
            const landing = step === "drop" && way === "drop" &&
                !map.isCollisionAt(Math.floor((x + translate.x) / TileSize), Math.floor((feet + translate.y) / TileSize))
            if (step === "rise" || (step === "drop" && !landing)) {
                result.collided = true
                result.adjust.x = x
                result.adjust.y = y
                result.collision.x = Math.floor((x + translate.x) / TileSize)
                result.collision.y = Math.floor((feet + translate.y) / TileSize)
                return
            }
        }
        mover(bounds, translate, result, adjust)
    }
}
